import { template as template_fd9b2463e5f141cfb5666b64daa1f11a } from "@ember/template-compiler";
import { ref } from 'ember-ref-bucket';
import { SELECT_ALL_ITEMS } from 'volta/components/bv-resource-list';
import { idVariation } from 'volta/utils/id';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import orElse from 'volta/helpers/or-else';
import classNames from 'volta/helpers/class-names';
import isPresent from 'volta/helpers/is-present';
import not from 'ember-truth-helpers/helpers/not';
import and from 'ember-truth-helpers/helpers/and';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import { fn } from '@ember/helper';
import trueStringIfPresent from 'volta/helpers/true-string-if-present';
import { hash } from '@ember/helper';
import stopPropagation from 'volta/helpers/stop-propagation';
// @ts-ignore
import createRef from 'ember-ref-bucket/modifiers/create-ref';
import { LinkTo } from '@ember/routing';
import BvUnstyledLink from 'volta/components/bv-unstyled-link';
import BvCheckbox from 'volta/components/bv-checkbox';
import BvBtnGroup from 'volta/components/bv-btn-group';
import BvPopover from 'volta/components/bv-popover';
import BvBtn from 'volta/components/bv-btn';
import BvMenu from 'volta/components/bv-menu';
import or from 'ember-truth-helpers/helpers/or';
import type { IBvResourceListContext } from 'volta/components/bv-resource-list';
import type { BvBtnArgs } from 'volta/components/bv-btn';
import type { ComponentLike, ContentValue } from '@glint/template';
import type RouterService from '@ember/routing/router-service';
import type Model from '@ember-data/model';
import type { IBvMenuItem } from 'volta/components/bv-menu';
function getIdentity(item?: {
    id?: string;
}) {
    return item && item.id;
}
interface BvResourceListItemApi {
    item: any;
    itemId: string | undefined | null;
    index: number | undefined;
    sortableGroupName?: string;
    sortableEnabled?: boolean;
}
export type BvResourceListShortcutBtn = BvBtnArgs & IBvMenuItem & {
    content?: string;
    hidden?: boolean;
    url?: string;
    classNames?: string;
};
export interface BvResourceListItemSignature {
    Args: {
        disabled?: boolean;
        label?: ContentValue;
        condensed?: boolean;
        isCurrent?: boolean;
        /* Unique identifier for the item */ itemId?: string | null;
        /* Item index */ index?: number;
        /* Visually hidden text for screen readers */ accessibilityLabel?: string;
        /* Id of the element the item onClick controls */ ariaControls?: string;
        /* Tells screen reader the controlled element is expanded */ ariaExpanded?: boolean;
        /* Media component or text */ media?: ContentValue | ComponentLike;
        persistActions?: boolean;
        /* Shortcut actions */ shortcutActions?: (BvResourceListShortcutBtn | undefined)[];
        /* On click event handler */ onClick?: (itemId?: string | null) => void;
        context: IBvResourceListContext;
        item: any;
        selectOnClick?: boolean;
        containerClasses?: string;
        /* Item link route info */ href?: string;
        route?: string;
        routeModel?: string | Model | null;
        routeModels?: string[] | Model[] | null;
        routeQuery?: TQueryParams;
        selected?: boolean;
        sortableGroupName?: string;
        sortableEnabled?: boolean;
    };
    Blocks: {
        default: [BvResourceListItemApi];
        media: [BvResourceListItemApi];
        actions: [BvResourceListItemApi];
    };
    Element: HTMLLIElement;
}
export default class BvResourceListItemComponent extends Component<BvResourceListItemSignature> {
    @service
    router: RouterService;
    @ref('containerNode')
    containerNode?: HTMLElement;
    @ref('buttonOverlay')
    buttonOverlay?: HTMLElement;
    @tracked
    focused = false;
    @tracked
    focusedInner = false;
    elementId = guidFor(this);
    checkboxId = idVariation(this.elementId, 'ResourceListItemCheckbox');
    overlayId = idVariation(this.elementId, 'OverlayId');
    get api() {
        return {
            item: this.args.item,
            itemId: this.args.itemId,
            index: this.args.index,
            sortableGroupName: this.args.sortableGroupName,
            sortableEnabled: this.args.sortableEnabled
        };
    }
    get isSelected() {
        const { context, itemId } = this.args;
        const selectedItems = (context && context.selectedItems) ?? [];
        return (selectedItems && ((Array.isArray(selectedItems) && selectedItems.some((i)=>getIdentity(i) === itemId)) || selectedItems === SELECT_ALL_ITEMS));
    }
    get linkInfo(): {
        route: any;
        routeModels: any[];
        routeQuery: any;
    } | undefined | {
        route: string | undefined;
        routeModels: (string | Model)[];
        routeQuery: TQueryParams | undefined;
    } {
        const { href, route, routeModel, routeModels, routeQuery } = this.args;
        try {
            if (href) {
                let routeInfo = this.router.recognize(href);
                if (routeInfo && routeInfo.localName !== 'not-found') {
                    if (routeInfo.localName === 'index' && routeInfo.parent) {
                        routeInfo = routeInfo.parent;
                    }
                    const paramRows = routeInfo.paramNames.map((paramName)=>routeInfo?.params?.[paramName]).filter(Boolean);
                    return {
                        route: routeInfo.name,
                        routeModels: paramRows,
                        routeQuery: routeInfo.queryParams
                    };
                }
                return undefined;
            } else {
                return {
                    route,
                    routeModels: routeModels?.length ? routeModels : routeModel ? [
                        routeModel
                    ] : [],
                    routeQuery
                };
            }
        } catch  {
            return undefined;
        }
    }
    @action
    handleAnchorFocus() {
        this.focused = true;
        this.focusedInner = true;
    }
    @action
    handleFocusedBlur() {
        this.focused = false;
        this.focusedInner = false;
    }
    @action
    handleFocus(event: FocusEvent) {
        if (event.target === this.buttonOverlay || (this.containerNode && event.target === this.containerNode.querySelector(`#${this.overlayId}`))) {
            this.focused = true;
            this.focusedInner = false;
        } else if (this.containerNode && this.containerNode.contains(event.target as Node)) {
            this.focused = true;
            this.focusedInner = true;
        }
    }
    @action
    handleBlur({ relatedTarget }: FocusEvent) {
        if (this.containerNode && relatedTarget instanceof Element && this.containerNode.contains(relatedTarget)) {
            return;
        }
        this.focused = false;
        this.focusedInner = false;
    }
    @action
    handleMouseOut() {
        if (this.focused) {
            this.focused = false;
            this.focusedInner = false;
        }
    }
    @action
    handleLargerSelectionArea(event: Event) {
        event.stopPropagation();
        this.handleSelection(!this.isSelected);
    }
    @action
    handleSelection(value: boolean) {
        const { itemId, context, item } = this.args;
        if (!itemId || !context.onSelectionChange) {
            return;
        }
        this.focused = value;
        this.focusedInner = value;
        context.onSelectionChange(value, itemId, item);
    }
    @action
    handleClick(event: MouseEvent) {
        const { href, item, itemId, onClick, context, selectOnClick } = this.args;
        const isLink = Boolean(this.linkInfo?.route ?? href);
        const anchor = this.containerNode && this.containerNode.querySelector('a');
        if (context.selectMode && !selectOnClick) {
            this.handleLargerSelectionArea(event);
            return;
        }
        if (anchor === event.target) {
            return;
        }
        if (selectOnClick) {
            context.onSelectionChange(Boolean(item), itemId, item, selectOnClick);
        } else {
            onClick?.(itemId);
        }
        if (isLink && anchor) {
            anchor.click();
        }
    }
    @action
    handleKeypress(event: KeyboardEvent) {
        const { onClick, context } = this.args;
        const { key } = event;
        if (onClick && key === 'Enter' && !context.selectMode) {
            onClick();
        }
    }
    compareEventNode(event: Event) {
        return this.args.onClick ? event.target === this.containerNode : (event.target as Element).tagName.toLowerCase() === 'a';
    }
    static{
        template_fd9b2463e5f141cfb5666b64daa1f11a(`
    {{! template-lint-disable no-positive-tabindex }}
    {{! template-lint-disable no-invalid-aria-attributes }}
    {{! template-lint-disable no-invalid-interactive }}
    <li class='bv-resource-list__list-item' ...attributes>
      <div
        class='bv-resource-list-item'
        data-test-id='item-wrapper'
        data-highlighted={{trueStringIfPresent @isCurrent}}
        data-condensed={{trueStringIfPresent @condensed}}
        data-selected={{trueStringIfPresent this.isSelected}}
        data-focused={{trueStringIfPresent this.focused}}
        data-focused-inner={{trueStringIfPresent this.focusedInner}}
        data-selectable={{trueStringIfPresent @context.selectable}}
        data-select-mode={{trueStringIfPresent @context.selectMode}}
        data-persist-actions={{trueStringIfPresent @persistActions}}
        {{on 'click' this.handleClick}}
        {{on 'focus' this.handleFocus}}
        {{on 'blur' this.handleBlur}}
        {{on 'mouseout' this.handleMouseOut}}
        {{on 'keyup' this.handleKeypress}}
        {{createRef 'containerNode' bucket=this}}
      >
        {{#let (if @context.loading -1 0) this.linkInfo as |tabIndex link|}}
          {{#if link}}
            {{! template-lint-disable no-positive-tabindex }}
            <LinkTo
              @route={{link.route}}
              @models={{link.routeModels}}
              @query={{orElse link.routeQuery (hash)}}
              id={{this.overlayId}}
              class='bv-resource-list-item__overlay'
              tabindex={{tabIndex}}
              aria-described-by={{@itemId}}
              aria-label={{@accessibilityLabel}}
              {{on 'click' this.handleClick}}
              {{on 'focus' this.handleAnchorFocus}}
              {{on 'blur' this.handleFocusedBlur}}
              {{createRef 'buttonOverlay' bucket=this}}
            />
          {{else if @href}}
            <BvUnstyledLink
              @url={{@href}}
              @external={{true}}
              id={{this.overlayId}}
              class='bv-resource-list-item__overlay'
              tabindex={{tabIndex}}
              aria-described-by={{@itemId}}
              aria-label={{@accessibilityLabel}}
              {{on 'click' this.handleClick}}
              {{on 'focus' this.handleAnchorFocus}}
              {{on 'blur' this.handleFocusedBlur}}
              {{createRef 'buttonOverlay' bucket=this}}
            />
          {{else}}
            <button
              id={{this.overlayId}}
              type='button'
              tabindex={{tabIndex}}
              class='bv-resource-list-item__overlay'
              aria-label={{@accessibilityLabel}}
              aria-controls={{@ariaControls}}
              aria-expanded='{{@ariaExpanded}}'
              {{on 'click' this.handleClick}}
              {{on 'focus' this.handleAnchorFocus}}
              {{on 'blur' this.handleFocusedBlur}}
              {{createRef 'buttonOverlay' bucket=this}}
            ></button>
          {{/if}}
        {{/let}}
        <div
          data-test-id='item-container'
          class={{classNames 'bv-resource-list-item__container' @containerClasses}}
          id={{@itemId}}
        >
          {{#let (or (isPresent @media) (has-block 'media')) as |hasMedia|}}
            {{#if (or hasMedia @context.selectable)}}
              <div class='bv-resource-list-item__owned'>
                {{#if @context.selectable}}
                  {{! template-lint-disable no-invalid-interactive }}
                  <div
                    class='bv-resource-list-item__handle'
                    data-test-id='larger-selection-area'
                    {{on 'click' this.handleLargerSelectionArea}}
                  >
                    {{! template-lint-disable no-invalid-interactive }}
                    <div
                      class='bv-resource-list-item__checkbox-wrapper'
                      {{on 'click' (stopPropagation)}}
                    >
                      <BvCheckbox
                        @id={{this.checkboxId}}
                        @label={{@label}}
                        @labelHidden={{true}}
                        @checked={{this.isSelected}}
                        @disabled={{@disabled}}
                        @onChange={{this.handleSelection}}
                      />
                    </div>
                  </div>
                {{/if}}
                {{#if hasMedia}}
                  <div class='bv-resource-list-item__media' data-test-id='media'>
                    {{#if (has-block 'media')}}
                      {{yield this.api to='media'}}
                    {{else}}
                      {{! @glint-ignore }}
                      {{@media}}
                    {{/if}}
                  </div>
                {{/if}}
              </div>
            {{/if}}
          {{/let}}
          {{#if (has-block)}}
            <div class='bv-resource-list-item__content'>
              {{yield this.api}}
            </div>
          {{/if}}
          {{#if (has-block 'actions')}}
            {{yield this.api to='actions'}}
          {{/if}}
          {{#if (and @shortcutActions (not @context.loading))}}
            {{#if @persistActions}}
              <div class='bv-resource-list-item__disclosure' {{on 'click' (stopPropagation)}}>
                <BvPopover @horizontalPosition='right' as |popover|>
                  {{! template-lint-disable no-bare-strings }}
                  <BvBtn
                    aria-label='Actions dropdown'
                    @subtle={{true}}
                    @iconLeft='ellipsis-horizontal'
                    @onClick={{popover.toggle}}
                    {{popover.trigger}}
                  />
                  <popover.Content>
                    <BvMenu @items={{@shortcutActions}} />
                  </popover.Content>
                </BvPopover>
              </div>
            {{else}}
              <div class='bv-resource-list-item__actions'>
                <BvBtnGroup @segmented={{true}} as |buttonGroup|>
                  {{#each @shortcutActions as |shortcutAction|}}
                    {{#unless shortcutAction.hidden}}
                      <buttonGroup.btn
                        class={{shortcutAction.classNames}}
                        @size={{or shortcutAction.size 'sm'}}
                        @subtle={{orElse shortcutAction.subtle @persistActions}}
                        @text={{shortcutAction.content}}
                        @accessibilityLabel={{shortcutAction.accessibilityLabel}}
                        @route={{shortcutAction.url}}
                        @color={{shortcutAction.color}}
                        @iconLeft={{shortcutAction.icon}}
                        @isLoading={{shortcutAction.isLoading}}
                        @tooltip={{shortcutAction.tooltip}}
                        @onClick={{stopPropagation (fn (optional shortcutAction.onClick))}}
                      />
                    {{/unless}}
                  {{/each}}
                </BvBtnGroup>
              </div>
            {{/if}}
          {{/if}}
        </div>
      </div>
    </li>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
declare module '@glint/environment-ember-loose/registry' {
    export default interface Registry {
        'BvResourceList::Item': typeof BvResourceListItemComponent;
        'bv-resource-list/item': typeof BvResourceListItemComponent;
    }
}
